import { IndividualSampleResult, SampleSummaryResult } from '../../../shared/models/result.model';
import { normalizeStatusValue } from '../../../shared/utils/assay-helper ';

export type FailureLevel = 'ALERT' | 'FAIL';

function toLevel(status: string | null | undefined): FailureLevel | null {
  const normalized = normalizeStatusValue(status);
  return normalized === 'ALERT' || normalized === 'FAIL' ? normalized : null;
}

/** The overall result (acceptance range, SD/CV and replicate count rolled together). */
export function resultLevel(sample: SampleSummaryResult | undefined): FailureLevel | null {
  return toLevel(sample?.resultStatus);
}

/** The concentration check alone; `resultStatus` also carries SD/CV and replicate-count outcomes. */
export function concentrationLevel(sample: SampleSummaryResult | undefined): FailureLevel | null {
  return toLevel(sample?.acceptanceRangeStatus);
}

/** Only plain FP/FN; the EQ-R/EQ-T variants are not false results. */
export function isFalseScore(sample: SampleSummaryResult | undefined): boolean {
  const score = (sample?.testScore ?? '').trim().toUpperCase();
  return score === 'FP' || score === 'FN';
}

/** `resultRepOk` is one of `OK SD`, `Alert SD`, `Alert CV1`, `Alert CV2`, `OK CV1`... or `-`. */
export function sdCvLevels(sample: SampleSummaryResult | undefined): { sd: FailureLevel | null; cv: FailureLevel | null } {
  const text = (sample?.resultRepOk ?? '').toUpperCase();
  const level = toLevel(text);
  return {
    sd: level && text.includes('SD') ? level : null,
    cv: level && text.includes('CV') ? level : null
  };
}

export function hasSdCvFailure(sample: SampleSummaryResult | undefined): boolean {
  const { sd, cv } = sdCvLevels(sample);
  return sd !== null || cv !== null;
}

/** Replicate count is short when the status is present and is not OK. */
export function isReplicateCountShort(sample: SampleSummaryResult | undefined): boolean {
  const status = (sample?.repCountOk ?? '').trim();
  return status !== '' && status !== '-' && status.toUpperCase() !== 'OK';
}

function toLimit(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** `35-45` for a two-sided spec, `> 12` / `< 47` for a one-sided one, null when there is no spec. */
export function formatSpecRange(low: number | string | null | undefined, high: number | string | null | undefined): string | null {
  const lo = toLimit(low);
  const hi = toLimit(high);
  if (lo !== null && hi !== null) {
    return `${lo}-${hi}`;
  }
  if (lo !== null) {
    return `> ${lo}`;
  }
  if (hi !== null) {
    return `< ${hi}`;
  }
  return null;
}

/** The range the concentration breached: the alert range for an ALERT, the fail range for a FAIL. */
export function breachedRange(sample: SampleSummaryResult | undefined): string | null {
  const level = concentrationLevel(sample);
  if (!sample || !level) {
    return null;
  }
  return level === 'FAIL'
    ? formatSpecRange(sample.failLow, sample.failHigh)
    : formatSpecRange(sample.alertLow, sample.alertHigh);
}

/** Both ranges, for the tooltip where there is room to show them. */
export function describeSpecRanges(sample: SampleSummaryResult | undefined): string {
  const alert = formatSpecRange(sample?.alertLow, sample?.alertHigh);
  const fail = formatSpecRange(sample?.failLow, sample?.failHigh);
  return [alert ? `Alert: ${alert}` : '', fail ? `Fail: ${fail}` : ''].filter(Boolean).join(' | ');
}

/** Replicates that contribute a value; discards are reported in Discard Reason instead. */
export function keptReps(sample: SampleSummaryResult | undefined): IndividualSampleResult[] {
  return (sample?.individualReps ?? []).filter((rep): rep is IndividualSampleResult => !!rep && !rep.isDiscarded);
}

export type FailureSectionId =
  | 'concentration' | 'result' | 'score' | 'sd' | 'cv' | 'sdCv'
  | 'individual' | 'replicateCount' | 'replicatesOk' | 'assigned' | 'discard';

export interface FailureSectionRow {
  key: string;
  label: string;
  /** Tooltip; screen only. */
  title: string;
  accession: string;
  repNum?: number;
}

export interface FailureSection {
  id: FailureSectionId;
  title: string;
  /** Roll-up status shown beside the title, where the section has one. */
  status?: 'concentration' | 'score' | 'sdCv' | 'replicates';
  rows: FailureSectionRow[];
}

/** Replicate number as the Calculations tab shows it (renumbered after discards). */
export function repNumber(rep: IndividualSampleResult): number {
  return rep.updatedRepNum ?? rep.repNum;
}

/** The backend fills `instrumentConc` instead of `calculatedConc` when the run is calculated on the instrument. */
export function repConcentration(rep: IndividualSampleResult): number | null {
  return rep.calculatedConc ?? rep.instrumentConc ?? null;
}
